import React, { useState, useEffect } from "react";
import {
  Record,
  Identifier,
  useDataProvider,
  useNotify,
  useTranslate,
} from "react-admin";
import { CircularProgress, Typography, Theme } from "@material-ui/core";
import { ShareLinkView } from "./ShareLinkView";
import { NewShareLink } from "./NewShareLink";
import "./ShareLinksForm.css";
import resourceNames from "../../../common/data/resourceNames";
import { LiveTrackTokenDto } from "../../../common/data/service-proxies";
import { useTheme } from "@material-ui/styles";

interface Props {
  data: Record;
}

const getShareLinkUrl = (id: Identifier, tenantId: string) => {
  return `${window.location.origin.toString()}/${
    resourceNames.LOCATIONS_LIVETRACK
  }/${id}/${tenantId}`;
};

export const ShareLinksForm = ({ data }: Props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const [tokens, setTokens] = useState<LiveTrackTokenDto[]>([]);
  const [error, setError] = useState<string>();
  const [vehicle, setVehicle] = useState<{ id: Identifier }>();
  const translate = useTranslate();

  useEffect(() => {
    setVehicle({ id: data.id });
  }, [data.id]);

  useEffect(() => {
    if (!vehicle) {
      return;
    }

    setLoading(true);
    dataProvider
      .getList<LiveTrackTokenDto & Record>(
        resourceNames.LOCATIONS_LIVETRACK_TOKENS,
        {
          pagination: { page: 1, perPage: 3 },
          sort: { field: "validFrom", order: "ASC" },
          filter: { vehicleId: vehicle.id },
        }
      )
      .then((result) => {
        if (result) {
          setTokens(result.data);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [data.id, dataProvider, vehicle]);

  const theme: Theme = useTheme();

  return (
    <div>
      <div className="share-links">
        {loading ? (
          <CircularProgress />
        ) : !!error ? (
          <Typography
            variant="body2"
            style={{ color: theme.palette.error.main }}
          >
            {translate(error, { _: error })}
          </Typography>
        ) : tokens!.length ? (
          tokens!.map(
            (token) =>
              token.id &&
              token.tenantId && (
                <ShareLinkView
                  key={token.id}
                  linkUrl={getShareLinkUrl(token.id, token.tenantId)}
                  {...token}
                  onDeleteClick={async () => {
                    setLoading(true);
                    try {
                      await dataProvider.delete(
                        resourceNames.LOCATIONS_LIVETRACK_TOKENS,
                        {
                          id: token.id!,
                          previousData: { ...token, id: token.id! },
                        }
                      );
                      setVehicle({ id: vehicle!.id });
                    } catch (e) {
                      if (e instanceof Error) {
                        notify(e.message, "error");
                      }
                      setLoading(false);
                    }
                  }}
                />
              )
          )
        ) : (
          <Typography variant="body2">
            {translate("resources.locations-current.labels.noShareLinks")}
          </Typography>
        )}
      </div>
      {!loading && tokens && tokens.length < 3 && (
        <NewShareLink
          vehicleId={data.id}
          onSaved={async (data) => {
            setLoading(true);
            try {
              await dataProvider.create(
                resourceNames.LOCATIONS_LIVETRACK_TOKENS,
                {
                  data,
                }
              );
              setVehicle({ id: data.vehicleId! });
            } catch (e) {
              if (e instanceof Error) {
                notify(e.message, "error");
              }
              setLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};
